
import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { Plus, HelpCircle, Pencil, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Solicitacao } from '@/types/solicitacao';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatBrazilTime } from '@/utils/timezone';
import { getSLAStatus, getStatusColor, getUrgenciaColor } from '@/utils/slaStatus';

type SortColumn = 'numero' | 'titulo' | 'status' | 'urgencia' | 'solicitante' | 'cliente' | 'grupo' | 'criado_em' | 'data_limite_resolucao' | 'data_fechamento' | 'sla' | 'atendente' | null;
type SortDirection = 'asc' | 'desc' | null;

interface RequisicoesTableProps {
  requisicoes: Solicitacao[];
  onEditRequisicao: (requisicao: Solicitacao) => void;
  onNewRequisicao: () => void;
}

const RequisicoesTable = ({ requisicoes, onEditRequisicao, onNewRequisicao }: RequisicoesTableProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const itemsPerPage = 10;

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') setSortDirection('desc');
      else if (sortDirection === 'desc') { setSortColumn(null); setSortDirection(null); }
      else setSortDirection('asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortColumn !== column) return <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-40" />;
    if (sortDirection === 'asc') return <ArrowUp className="h-3 w-3 ml-1 inline" />;
    return <ArrowDown className="h-3 w-3 ml-1 inline" />;
  };

  const sortedRequisicoes = useMemo(() => {
    if (!sortColumn || !sortDirection) return requisicoes;
    const sorted = [...requisicoes].sort((a, b) => {
      let valA = '', valB = '';
      switch (sortColumn) {
        case 'numero': valA = a.numero || ''; valB = b.numero || ''; break;
        case 'titulo': valA = a.titulo || ''; valB = b.titulo || ''; break;
        case 'status': valA = a.status || ''; valB = b.status || ''; break;
        case 'urgencia': valA = a.urgencia || ''; valB = b.urgencia || ''; break;
        case 'solicitante': valA = a.solicitante?.name || ''; valB = b.solicitante?.name || ''; break;
        case 'cliente': valA = a.cliente?.name || ''; valB = b.cliente?.name || ''; break;
        case 'grupo': valA = a.grupo_responsavel?.name || ''; valB = b.grupo_responsavel?.name || ''; break;
        case 'atendente': valA = a.atendente?.name || ''; valB = b.atendente?.name || ''; break;
        case 'criado_em': valA = a.criado_em || ''; valB = b.criado_em || ''; break;
        case 'data_limite_resolucao': valA = a.data_limite_resolucao || ''; valB = b.data_limite_resolucao || ''; break;
        case 'data_fechamento': valA = a.data_fechamento || ''; valB = b.data_fechamento || ''; break;
        case 'sla': valA = getSLAStatus(a).label; valB = getSLAStatus(b).label; break;
      }
      const cmp = valA.localeCompare(valB, 'pt-BR', { numeric: true });
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [requisicoes, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedRequisicoes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRequisicoes = sortedRequisicoes.slice(startIndex, endIndex);

  if (requisicoes.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16">
          <HelpCircle className="h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Nenhuma requisição encontrada
          </h3>
          <p className="text-gray-500 text-center mb-6">
            Comece criando sua primeira requisição de serviço.
          </p>
          <Button onClick={onNewRequisicao}>
            <Plus className="mr-2 h-4 w-4" />
            Criar Primeira Requisição
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lista de Requisições</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="cursor-pointer select-none" onClick={() => handleSort('numero')}>Número <SortIcon column="numero" /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => handleSort('titulo')}>Título <SortIcon column="titulo" /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => handleSort('status')}>Status <SortIcon column="status" /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => handleSort('urgencia')}>Urgência <SortIcon column="urgencia" /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => handleSort('solicitante')}>Solicitante <SortIcon column="solicitante" /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => handleSort('cliente')}>Cliente <SortIcon column="cliente" /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => handleSort('grupo')}>Grupo <SortIcon column="grupo" /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => handleSort('criado_em')}>Data Criação <SortIcon column="criado_em" /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => handleSort('data_limite_resolucao')}>Data Limite Resolução <SortIcon column="data_limite_resolucao" /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => handleSort('data_fechamento')}>Data de Fechamento <SortIcon column="data_fechamento" /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => handleSort('sla')}>SLA <SortIcon column="sla" /></TableHead>
              <TableHead className="cursor-pointer select-none" onClick={() => handleSort('atendente')}>Atendente <SortIcon column="atendente" /></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentRequisicoes.map((requisicao) => {
              const slaStatus = getSLAStatus(requisicao);
              const IconComponent = slaStatus.icon;
              
              return (
                <TableRow key={requisicao.id}>
                  <TableCell>
                    <button
                      onClick={() => onEditRequisicao(requisicao)}
                      className="font-bold text-black hover:text-gray-700 hover:underline transition-colors cursor-pointer"
                    >
                      {requisicao.numero}
                    </button>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {requisicao.titulo}
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(requisicao.status)}>
                      {requisicao.status.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={getUrgenciaColor(requisicao.urgencia)}>
                      {requisicao.urgencia}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {requisicao.solicitante?.name || '-'}
                  </TableCell>
                  <TableCell>
                    {requisicao.cliente?.name || '-'}
                  </TableCell>
                  <TableCell>
                    {requisicao.grupo_responsavel?.name || '-'}
                  </TableCell>
                  <TableCell>
                    {formatBrazilTime(requisicao.criado_em, 'dd/MM/yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    {requisicao.data_limite_resolucao ? formatBrazilTime(requisicao.data_limite_resolucao, 'dd/MM/yyyy HH:mm') : '-'}
                  </TableCell>
                  <TableCell>
                    {requisicao.data_fechamento ? formatBrazilTime(requisicao.data_fechamento, 'dd/MM/yyyy HH:mm') : '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <IconComponent className="h-4 w-4" />
                      <Badge className={slaStatus.color}>
                        {slaStatus.label}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    {requisicao.atendente?.name || '-'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        
        {totalPages > 1 && (
          <div className="mt-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      onClick={() => setCurrentPage(page)}
                      isActive={currentPage === page}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                
                <PaginationItem>
                  <PaginationNext 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RequisicoesTable;
